import React from "react"
import * as TablerIcons from "@tabler/icons-react"
import {AlertCategory} from "@/types/database.types"

// ==============================================================================
// ALERT CATEGORY UTILITIES
// ==============================================================================

// Icon mapping for each alert category
export const AlertCategoryIconMap: Record<AlertCategory, React.ReactNode> = {
  non_compliant_communications: <TablerIcons.IconMessageCircle size={20} />,
  advertising_marketing_violations: <TablerIcons.IconAd2 size={20} />,
  client_investor_protection: <TablerIcons.IconShieldCheck size={20} />,
  personal_trading_employee_conduct: <TablerIcons.IconUserCheck size={20} />,
  behavioral_sentiment_analysis: <TablerIcons.IconChartLine size={20} />,
  user_defined: <TablerIcons.IconUser size={20} />,
} satisfies Record<AlertCategory, React.ReactNode>

// Base color mapping for each alert category
export const AlertCategoryBaseColors: Record<AlertCategory, string> = {
  non_compliant_communications: "blue",
  advertising_marketing_violations: "red",
  client_investor_protection: "purple",
  personal_trading_employee_conduct: "yellow",
  behavioral_sentiment_analysis: "orange",
  user_defined: "green",
} satisfies Record<AlertCategory, string>

// Helper function to generate color classes from base color
export const getAlertCategoryColors = (category: AlertCategory) => {
  const baseColor = AlertCategoryBaseColors[category]
  return {
    bg: `bg-${baseColor}-500/10`,
    text: `text-${baseColor}-500`,
    border: `border-${baseColor}-500`,
    bgFull: `bg-${baseColor}-500`,
  }
}

// Backward compatibility - keep the same interface
export const AlertCategoryColorMap: Record<
  AlertCategory,
  {
    bg: string
    text: string
    border: string
    bgFull: string
  }
> = Object.fromEntries(
  Object.keys(AlertCategoryBaseColors).map((category) => [
    category,
    getAlertCategoryColors(category as AlertCategory),
  ])
) as Record<
  AlertCategory,
  {bg: string; text: string; border: string; bgFull: string}
>

// Helper function to get icon with custom size and className
export const getAlertCategoryIcon = (
  category: AlertCategory,
  size: number = 20,
  className?: string
): React.ReactNode => {
  // Simple icon mapping with fallback
  const icons: Record<AlertCategory, React.ComponentType<any>> = {
    non_compliant_communications: TablerIcons.IconMessageCircle,
    advertising_marketing_violations: TablerIcons.IconAd2,
    client_investor_protection: TablerIcons.IconShieldCheck,
    personal_trading_employee_conduct: TablerIcons.IconUserCheck,
    behavioral_sentiment_analysis: TablerIcons.IconChartLine,
    user_defined: TablerIcons.IconBuilding,
  } satisfies Record<AlertCategory, React.ComponentType<any>>

  const IconComponent = icons[category] || TablerIcons.IconAlertCircle

  return <IconComponent size={size} className={className} />
}

// Helper function to format category names for display
export const formatAlertCategoryName = (category: AlertCategory): string => {
  const nameMap: Record<AlertCategory, string> = {
    non_compliant_communications: "Non-Compliant Communications",
    advertising_marketing_violations: "Advertising / Marketing Violations",
    client_investor_protection: "Client / Investor Protection",
    personal_trading_employee_conduct: "Personal Trading / Employee Conduct",
    behavioral_sentiment_analysis: "Behavioral and Sentiment Analysis",
    user_defined: "Organization-Specific Policy",
  } satisfies Record<AlertCategory, string>

  return nameMap[category] || "Unknown Category"
}

// Helper function to format alert definition names (capitalize and clean up)
export const formatAlertName = (name: string): string => {
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

// Helper function to get Badge variant for alert category
export const getAlertCategoryBadgeVariant = (
  category: AlertCategory
): "red" | "green" | "blue" | "orange" | "yellow" | "purple" | "gray" => {
  const baseColor = AlertCategoryBaseColors[category]

  // Map to Badge variants (Badge component supports these specific variants)
  const validVariants = [
    "red",
    "green",
    "blue",
    "orange",
    "yellow",
    "purple",
    "gray",
  ] as const

  return validVariants.includes(baseColor as any)
    ? (baseColor as (typeof validVariants)[number])
    : "gray"
}
